import { ILogin } from "@interfaces";
import { memo } from "react";
import * as Yup from "yup";
import { useYup } from "@validations";
import { Input, Form, Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { SharedButton, StyledAuth } from "@components";
import { clinicLogin, login, selectApp, setLoading, useAppDispatch } from "@redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNotify, getApiErrorMessage } from "@utils";
import { useSelector } from "react-redux";
import { PATH_FORGOT_PASSWORD, PATH_LOGIN_BY_PHONE, enumAdminRole, enumSubdomain } from "@configs";
import { Link } from "react-router-dom";

const initValue: ILogin = {
  email: "",
  password: "",
};

export const LoginForm = memo(() => {
  const { YupLogin } = useYup();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { success, error } = useNotify();
  // form and validation
  const loginSchema = Yup.object().shape(YupLogin);
  const [loginForm] = Form.useForm();
  const { domain } = useSelector(selectApp);
  const onFinish = async (values: ILogin) => {
    let res;
    try {
      dispatch(setLoading(true));
      if (domain === enumSubdomain.CLINIC || domain === enumSubdomain.STG_CLINIC) {
        res = await dispatch(clinicLogin(values));
      } else {
        res = await dispatch(login(values));
      }
      unwrapResult(res);
      success(t("message.login.success"));
    } catch (err: any) {
      error(getApiErrorMessage(err, t("message.fetch.fail")));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await loginSchema.validateSyncAt(field, { [field]: value });
    },
  };
  return (
    <StyledAuth>
      <header>
        <h4>{t("page.log_in")}</h4>
      </header>
      <Form
        form={loginForm}
        name="basic"
        initialValues={initValue}
        onFinish={onFinish}
        layout="vertical"
      >
        <Row gutter={{ md: 54, lg: 54, sm: 0 }}>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.email")} name="email" rules={[yupSync]}>
              <Input type="email" data-testid="login-email" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54, sm: 0 }}>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.password")} name="password" rules={[yupSync]}>
              <Input.Password type="password" data-testid="login-password" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54, sm: 0 }}>
          <Col lg={8} md={8} sm={8} xs={8} />
          <Col lg={8} md={8} sm={8} xs={8}>
          <Form.Item>
            <SharedButton type="primary" htmlType="submit" size="middle" data-testid="login-submit">
              {t("page.log_in")}
            </SharedButton>
            </Form.Item>
          </Col>
          <Col lg={8} md={8} sm={8} xs={8} />
        </Row>
        <Row gutter={{ md: 54, lg: 54, sm: 0 }}>
        <Col lg={8} md={8} sm={8} xs={8} />
          <Col lg={8} md={8} sm={8} xs={8}>
            <Form.Item>
            <Link to={PATH_LOGIN_BY_PHONE}>{t("page.forgot_password")}</Link>
            </Form.Item>
          </Col>
          <Col lg={8} md={8} sm={8} xs={8} />
        </Row>
      </Form>
    </StyledAuth>
  );
});
